import { CheckCircle2, Loader2, ServerOff, SignalLow, WifiOff } from "lucide-react";
import { useState, type ReactNode } from "react";

import { useConnectionBanner } from "@/lib/api/connection-banner";
import { retryServerNow } from "@/lib/api/server-reachability";
import { cn } from "@/lib/utils";

/**
 * Bandeau d'état de la connexion et de l'application, en haut de l'écran.
 *
 * Un seul message à la fois (voir `useConnectionBanner`) : hors ligne, serveur
 * indisponible, connexion rétablie, connexion lente. La nouvelle version est
 * signalée dans le pied de page (`UpdateNotice`).
 *
 * DEUX CAUSES DE COUPURE, DEUX MESSAGES. « Votre poste n'a plus de réseau » et
 * « le serveur est momentanément absent » appellent des gestes opposés : dans
 * le premier cas on va voir le Wi-Fi ou la box, dans le second il n'y a rien à
 * faire — un déploiement dure une minute ou deux et la reprise est automatique.
 * En offline-first, l'application reste utilisable : les saisies sont mises en
 * file et le compteur rappelle combien restent à envoyer.
 */
export function ConnectionBanner() {
  const state = useConnectionBanner();
  const [retrying, setRetrying] = useState(false);
  if (!state) return null;

  const retry = async () => {
    setRetrying(true);
    try {
      await retryServerNow();
    } finally {
      setRetrying(false);
    }
  };

  const tone = state.kind === "restored" ? "bg-success text-white" : "bg-warning text-white";

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-4 py-2 text-center text-sm font-medium",
        tone,
      )}
    >
      {state.kind === "offline" && (
        <>
          <WifiOff className="h-4 w-4 shrink-0" aria-hidden />
          <span>Hors ligne</span>
          <span className="font-normal opacity-90">
            — les données affichées peuvent ne pas être à jour ; vos saisies seront synchronisées
            au retour du réseau.
          </span>
          <PendingBadge count={state.pending} />
        </>
      )}

      {state.kind === "server-down" && (
        <>
          <ServerOff className="h-4 w-4 shrink-0" aria-hidden />
          <span>Serveur indisponible</span>
          <span className="font-normal opacity-90">
            — mise à jour ou interruption momentanée ; vous pouvez continuer à saisir, tout sera
            enregistré dès le retour. Rien à faire de votre côté.
          </span>
          <PendingBadge count={state.pending} />
          <BannerButton onClick={() => void retry()} busy={retrying}>
            {retrying ? "Nouvelle tentative…" : "Réessayer"}
          </BannerButton>
        </>
      )}

      {state.kind === "restored" && (
        <>
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
          <span>Connexion rétablie</span>
          {state.pending > 0 && (
            <span className="font-normal opacity-90">
              — envoi de vos saisies en attente ({state.pending}).
            </span>
          )}
        </>
      )}

      {state.kind === "slow" && (
        <>
          <SignalLow className="h-4 w-4 shrink-0" aria-hidden />
          <span>Connexion lente</span>
          <span className="font-normal opacity-90">— le chargement peut prendre un moment.</span>
        </>
      )}
    </div>
  );
}

function PendingBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="ml-1 inline-flex items-center bg-black/20 px-2 py-0.5 text-xs font-semibold">
      {count} opération{count > 1 ? "s" : ""} en attente
    </span>
  );
}

function BannerButton({
  onClick,
  busy,
  children,
}: {
  onClick: () => void;
  busy: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="ml-1 inline-flex min-h-8 items-center gap-1.5 border border-current px-3 py-1 text-xs font-semibold hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-80"
    >
      {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
      {children}
    </button>
  );
}
